import React, { useEffect, useState } from "react";
import { ModalProps } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import { BaseButton, Error, Switch, TextInput } from "../../../libs";
import { CouponCode } from "../types";
import * as Styled from "./RedemptionLimitModal.styled";

interface Props extends ModalProps {
  coupon: CouponCode | null;
  onSave: (maxRedemptions: number | null) => Promise<void>;
}

export const RedemptionLimitModal: React.FC<Props> = ({ coupon, onSave, ...props }) => {
  const [unlimited, setUnlimited] = useState(true);
  const [limit, setLimit] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!props.show || !coupon) return;
    setUnlimited(coupon.maxRedemptions == null);
    setLimit(coupon.maxRedemptions == null ? "" : String(coupon.maxRedemptions));
    setError("");
  }, [props.show, coupon]);

  const close = () => {
    if (saving) return;
    setError("");
    props.onHide?.();
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!coupon) return;
    const value = Number(limit);
    if (!unlimited && (!Number.isInteger(value) || value <= 0)) {
      setError("* Enter a positive whole number");
      return;
    }
    if (!unlimited && value < (coupon.redemptionCount || 0)) {
      setError(
        `* Limit cannot be lower than ${coupon.redemptionCount || 0} redemptions already used`
      );
      return;
    }
    setSaving(true);
    try {
      await onSave(unlimited ? null : value);
      props.onHide?.();
    } catch {
      // The parent reports API errors and keeps the modal open.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Styled.LimitModal {...props} centered keyboard={false} backdrop="static">
      <Styled.ModalWrapper>
        <Styled.Header>
          <div>
            <Styled.ModalTitle>Edit Redemption Limit</Styled.ModalTitle>
            <Styled.Subtitle>
              {coupon
                ? `${coupon.code} has been redeemed ${coupon.redemptionCount || 0} time${(coupon.redemptionCount || 0) === 1 ? "" : "s"}.`
                : ""}
            </Styled.Subtitle>
          </div>
          <Styled.CloseButton type="button" onClick={close} aria-label="Close">
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>
        <form onSubmit={submit}>
          <Styled.SwitchRow>
            <Styled.SwitchCopy>
              <Styled.SwitchTitle>Unlimited Redemptions</Styled.SwitchTitle>
              <Styled.HelpText>
                Allow the coupon to continue without a redemption cap.
              </Styled.HelpText>
            </Styled.SwitchCopy>
            <Switch
              checked={unlimited}
              onChange={(event) => {
                setUnlimited(event.target.checked);
                setError("");
              }}
            />
          </Styled.SwitchRow>
          {!unlimited && (
            <Styled.Field>
              {error && <Error message={error} />}
              <TextInput
                fieldName="maxRedemptions"
                fieldLabel="Maximum Redemptions *"
                fieldType="number"
                placeholder="100"
                fieldValue={limit}
                onChangeHandler={(event) => {
                  setLimit((event.target as HTMLInputElement).value);
                  setError("");
                }}
                hasError={!!error}
              />
              <Styled.HelpText>
                The limit may equal the current usage, which prevents any further redemptions.
              </Styled.HelpText>
            </Styled.Field>
          )}
          <Styled.ImmutableNote>
            The discount amount, duration, code, and eligible plans stay unchanged. Those values
            belong to the underlying Stripe coupon.
          </Styled.ImmutableNote>
          <Styled.Footer>
            <Styled.ButtonSlot>
              <BaseButton type="button" variant="secondary" onClick={close} disabled={saving}>
                Cancel
              </BaseButton>
            </Styled.ButtonSlot>
            <Styled.ButtonSlot>
              <BaseButton type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Limit"}
              </BaseButton>
            </Styled.ButtonSlot>
          </Styled.Footer>
        </form>
      </Styled.ModalWrapper>
    </Styled.LimitModal>
  );
};
